import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Slideshow controls custom element that handles progress bar updates
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [progressBar] - The progress bar element
 * @property {HTMLElement} [progressContainer] - The progress container element
 *
 * @extends {Component<Refs>}
 */
export class SlideshowControls extends Component {
  requiredRefs = [];
  #scrollHandler = null;
  #slideshow = null;
  #scroller = null;
  #observer = null;
  #progressInterval = null;
  #thumbnailScrollHandler = null;
  #thumbnailContainer = null;
  #prevArrow = null;
  #nextArrow = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Find and store reference to parent slideshow component
    this.#slideshow = this.closest('slideshow-component');

    // Wait for refs to be populated
    requestAnimationFrame(() => {
      // Try to find progress bar by ref or by class
      const progressBar = this.refs.progressBar || this.querySelector('.slideshow-controls__progress-bar');
      const progressContainer = this.refs.progressContainer || this.querySelector('.slideshow-controls__progress');
      
      if (progressBar && progressContainer) {
        // Store refs manually if not found via refs system
        if (!this.refs.progressBar) this.refs.progressBar = progressBar;
        if (!this.refs.progressContainer) this.refs.progressContainer = progressContainer;
        
        this.#setupProgressBar();
      }

      // Setup thumbnail arrows and visible count if they exist
      this.#setupThumbnailArrows();
      this.#setupThumbnailVisibleCount();
    });
  }

  /**
   * Updates the progress bar based on current slide index or scroll position
   */
  #updateProgress() {
    if (!this.#slideshow || !this.#scroller) return;
    
    const progressBar = this.refs.progressBar || this.querySelector('.slideshow-controls__progress-bar');
    if (!progressBar) return;

    const slides = this.#slideshow.querySelectorAll('slideshow-slide');
    const totalSlides = slides.length;
    if (totalSlides === 0) {
      progressBar.style.width = '0%';
      return;
    }

    let progress = 0;

    // Try to get current index from slideshow's current property first
    if (this.#slideshow.current !== undefined && typeof this.#slideshow.current === 'number') {
      const currentIndex = Math.max(0, Math.min(this.#slideshow.current, totalSlides - 1));
      progress = ((currentIndex + 1) / totalSlides) * 100;
    } else {
      // Fallback: calculate based on scroll position
      const scrollerRect = this.#scroller.getBoundingClientRect();
      const isHorizontal = this.#scroller.scrollWidth > this.#scroller.clientWidth;
      
      if (isHorizontal) {
        const scrollSize = this.#scroller.scrollWidth;
        const clientSize = this.#scroller.clientWidth;
        const scrollPosition = this.#scroller.scrollLeft;
        const maxScroll = scrollSize - clientSize;
        
        if (maxScroll > 0) {
          progress = (scrollPosition / maxScroll) * 100;
        } else {
          // If no scrolling needed, we're at the first slide
          progress = (1 / totalSlides) * 100;
        }
      } else {
        // Vertical scrolling
        const scrollSize = this.#scroller.scrollHeight;
        const clientSize = this.#scroller.clientHeight;
        const scrollPosition = this.#scroller.scrollTop;
        const maxScroll = scrollSize - clientSize;
        
        if (maxScroll > 0) {
          progress = (scrollPosition / maxScroll) * 100;
        } else {
          progress = (1 / totalSlides) * 100;
        }
      }

      // Clamp progress between 0 and 100
      progress = Math.max(0, Math.min(100, progress));
    }

    progressBar.style.width = `${progress}%`;
  }

  /**
   * Forwards select calls to the parent slideshow component
   * This allows thumbnails with on:click="/select/..." to work
   * @param {number|string} input - The index or id of the slide to select
   * @param {Event} [event] - The event that triggered the selection
   * @param {Object} [options] - The options for the selection
   */
  select(input, event, options) {
    if (!this.#slideshow) {
      this.#slideshow = this.closest('slideshow-component');
    }
    if (this.#slideshow && typeof this.#slideshow.select === 'function') {
      return this.#slideshow.select(input, event, options);
    }
  }

  /**
   * Scrolls thumbnails in the specified direction
   * @param {string} direction - The direction ('previous' or 'next')
   * @param {Event} [event] - The event that triggered the scroll
   */
  scrollThumbnails(direction, event) {

    if (!this.#thumbnailContainer) {
      this.#thumbnailContainer = this.refs.thumbnailsContainer || 
        this.querySelector('.slideshow-controls__thumbnails-container');
    }
    
    if (!this.#thumbnailContainer) return;

    const scrollAmount = this.#thumbnailContainer.clientHeight * 0.8; // Scroll 80% of visible height
    const currentScroll = this.#thumbnailContainer.scrollTop;
    const maxScroll = this.#thumbnailContainer.scrollHeight - this.#thumbnailContainer.clientHeight;

    let targetScroll;
    if (direction === 'next') {
      targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
    } else {
      targetScroll = Math.max(currentScroll - scrollAmount, 0);
    }

    this.#thumbnailContainer.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
  }

  /**
   * Updates the visibility of thumbnail arrows based on scroll position
   */
  #updateThumbnailArrows() {
    if (!this.#thumbnailContainer) return;

    const scrollTop = this.#thumbnailContainer.scrollTop;
    const scrollHeight = this.#thumbnailContainer.scrollHeight;
    const clientHeight = this.#thumbnailContainer.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    if (this.#prevArrow) {
      this.#prevArrow.style.display = scrollTop <= 0 ? 'none' : 'flex';
    }
    if (this.#nextArrow) {
      this.#nextArrow.style.display = scrollTop >= maxScroll - 1 ? 'none' : 'flex'; // -1 for rounding
    }
  }

  /**
   * Sets up thumbnail arrow navigation
   */
  #setupThumbnailArrows() {
    this.#prevArrow = this.querySelector('.slideshow-controls__thumbnail-arrow--prev');
    this.#nextArrow = this.querySelector('.slideshow-controls__thumbnail-arrow--next');
    this.#thumbnailContainer = this.refs.thumbnailsContainer || 
      this.querySelector('.slideshow-controls__thumbnails-container');

    if (!this.#thumbnailContainer || (!this.#prevArrow && !this.#nextArrow)) {
      return; // No thumbnail container or arrows
    }

    // Create scroll handler with throttling
    let scrollTimeout = null;
    this.#thumbnailScrollHandler = () => {
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = requestAnimationFrame(() => {
        this.#updateThumbnailArrows();
      });
    };

    // Listen to scroll events to update arrow states
    this.#thumbnailContainer.addEventListener('scroll', this.#thumbnailScrollHandler, { passive: true });

    // Initial update
    requestAnimationFrame(() => {
      this.#updateThumbnailArrows();
    });

    // Also check on resize
    const resizeObserver = new ResizeObserver(() => {
      this.#updateThumbnailArrows();
      this.#updateThumbnailVisibleCount();
    });
    resizeObserver.observe(this.#thumbnailContainer);
    
    // Also observe the main slideshow for height changes
    const mediaGallery = this.closest('media-gallery');
    if (mediaGallery) {
      const slideshow = mediaGallery.querySelector('slideshow-component');
      if (slideshow) {
        resizeObserver.observe(slideshow);
      }
    }
    
    this.#observer = resizeObserver;
  }

  /**
   * Sets up thumbnail scaling to match main image height
   */
  #setupThumbnailVisibleCount() {
    if (!this.#thumbnailContainer) return;

    // Find the main slideshow to measure its height
    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!slideshow) return;

    const visibleCount = Math.max(3, parseInt(getComputedStyle(this).getPropertyValue('--thumbnail-visible-count') || '4', 10));
    const gap = parseFloat(getComputedStyle(this.#thumbnailContainer).getPropertyValue('--thumbnail-spacing') || 
                          getComputedStyle(this).getPropertyValue('--gap-xs') || '8px');
    const gapValue = parseFloat(gap) || 8;

    // Wait for layout to settle
    requestAnimationFrame(() => {
      // Measure the main slideshow container height
      const slideshowRect = slideshow.getBoundingClientRect();
      const mainImageHeight = slideshowRect.height;

      if (mainImageHeight === 0) {
        // Retry if not ready yet
        setTimeout(() => this.#setupThumbnailVisibleCount(), 100);
        return;
      }

      // Calculate thumbnail height: (main image height - (gaps × (count - 1))) / count
      const totalGaps = gapValue * (visibleCount - 1);
      const availableHeight = mainImageHeight - totalGaps;
      const thumbnailHeight = availableHeight / visibleCount;

      // Get aspect ratio from first thumbnail or use default
      const firstThumbnail = this.#thumbnailContainer.querySelector('.slideshow-controls__thumbnail');
      if (!firstThumbnail) return;

      const aspectRatio = parseFloat(getComputedStyle(firstThumbnail).getPropertyValue('--aspect-ratio') || '1');
      
      // Calculate thumbnail width based on height and aspect ratio
      const thumbnailWidth = thumbnailHeight * aspectRatio;

      // Apply calculated width to all thumbnails
      const thumbnails = this.#thumbnailContainer.querySelectorAll('.slideshow-controls__thumbnail');
      thumbnails.forEach(thumb => {
        thumb.style.width = `${thumbnailWidth}px`;
        thumb.style.minWidth = `${thumbnailWidth}px`;
        thumb.style.maxWidth = `${thumbnailWidth}px`;
      });

      // Set container max-height to match main image
      this.#thumbnailContainer.style.maxHeight = `${mainImageHeight}px`;
    });
  }

  /**
   * Updates the visible count when thumbnails resize
   */
  #updateThumbnailVisibleCount() {
    this.#setupThumbnailVisibleCount();
  }

  /**
   * Sets up the progress bar by listening to slideshow select events and scroll events
   */
  #setupProgressBar() {
    // Find the parent slideshow component if not already found
    if (!this.#slideshow) {
      this.#slideshow = this.closest('slideshow-component');
    }
    if (!this.#slideshow) {
      console.warn('SlideshowControls: Could not find parent slideshow-component');
      return;
    }

    // Get the scroller element from the slideshow (slideshow-slides)
    this.#scroller = this.#slideshow.querySelector('slideshow-slides');
    if (!this.#scroller) {
      console.warn('SlideshowControls: Could not find slideshow-slides element');
      return;
    }

    // Get total number of slides
    const slides = this.#slideshow.querySelectorAll('slideshow-slide');
    const totalSlides = slides.length;
    if (totalSlides === 0) {
      console.warn('SlideshowControls: No slides found');
      return;
    }

    // Create scroll handler with throttling
    let scrollTimeout = null;
    this.#scrollHandler = () => {
      // Clear existing timeout
      if (scrollTimeout) {
        cancelAnimationFrame(scrollTimeout);
      }
      // Throttle updates using requestAnimationFrame
      scrollTimeout = requestAnimationFrame(() => {
        this.#updateProgress();
      });
    };

    // Listen to scroll events for real-time updates
    this.#scroller.addEventListener('scroll', this.#scrollHandler, { passive: true });

    // Listen for slideshow select events
    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, (event) => {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        this.#updateProgress();
      });
    });

    // Initialize progress bar
    requestAnimationFrame(() => {
      this.#updateProgress();
    });

    // Also set up a periodic check as a fallback (every 200ms for smoother updates)
    this.#progressInterval = setInterval(() => {
      this.#updateProgress();
    }, 200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up scroll listener
    if (this.#scrollHandler && this.#scroller) {
      this.#scroller.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }

    // Clean up thumbnail scroll listener
    if (this.#thumbnailScrollHandler && this.#thumbnailContainer) {
      this.#thumbnailContainer.removeEventListener('scroll', this.#thumbnailScrollHandler);
      this.#thumbnailScrollHandler = null;
    }

    // Clean up observer
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    // Clean up interval
    if (this.#progressInterval) {
      clearInterval(this.#progressInterval);
      this.#progressInterval = null;
    }
  }
}

if (!customElements.get('slideshow-controls')) {
  customElements.define('slideshow-controls', SlideshowControls);
}

